import { formatPriceLike, parsePriceNumber } from './priceFormat';

/**
 * PRIX PAR ZONE D'IMPRESSION (mode e-commerce). L'hôte tarife la personnalisation
 * par zone, et peut proposer des COMBINAISONS : un prix groupé pour un ensemble de
 * zones (ex. « Recto » = [recto], « Recto + Verso » = [recto, verso]), chaque prix
 * étant un supplément unitaire TTC. Il envoie la liste via `sendZonePrices` ; le FO
 * facture la façon la moins chère de couvrir les zones réellement décorées, chaque
 * zone une seule fois (recto + verso + manche = combinaison recto + verso + manche).
 *
 * Les ids de zone sont les uuid des zones UV du produit (Zone.id), les mêmes que
 * ceux configurés côté hôte : aucune table de correspondance n'est nécessaire.
 */
export interface ZonePrice {
    /** Id de la combinaison côté hôte, renvoyé tel quel dans `addToCart`. */
    id: string;
    name: string;
    /** Supplément unitaire formaté par l'hôte (ex. "2,52 €"). */
    price: string;
    zoneIds: string[];
    /** Libellés fournis par l'hôte, en repli des libellés du produit. */
    zones: Array<{ id: string; label: string }>;
}

export interface ZonePriceResolution {
    /**
     * Entrée facturée quand une seule couvre le design ; null si aucune zone, si
     * rien ne convient, ou si le design est facturé en plusieurs entrées (`pieces`).
     */
    combination: ZonePrice | null;
    /** Entrées facturées, qui couvrent ensemble les zones décorées (vide sans supplément). */
    pieces: ZonePrice[];
    /** Supplément unitaire numérique : la somme des `pieces`. */
    surcharge: number;
    /** Des zones sont décorées mais aucune combinaison ne les couvre toutes. */
    uncovered: boolean;
}

function asString(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

/**
 * Valide la liste reçue de l'hôte (frame tierce : rien n'est garanti). Les entrées
 * sans id, sans prix lisible ou sans zone sont écartées plutôt que de fausser un total.
 */
export function normalizeZonePrices(raw: unknown): ZonePrice[] {
    if (!Array.isArray(raw)) {
        return [];
    }

    return raw.flatMap((entry): ZonePrice[] => {
        if (!entry || typeof entry !== 'object') {
            return [];
        }
        const item = entry as Record<string, unknown>;
        const id = asString(item.id);
        const price = asString(item.price);
        const zoneIds = Array.isArray(item.zoneIds) ? [...new Set(item.zoneIds.map(asString).filter(Boolean))] : [];

        if (!id || parsePriceNumber(price) == null || zoneIds.length === 0) {
            return [];
        }

        const zones = Array.isArray(item.zones)
            ? item.zones.flatMap((zone) => {
                  const z = (zone ?? {}) as Record<string, unknown>;
                  const zoneId = asString(z.id);
                  return zoneId ? [{ id: zoneId, label: asString(z.label) || zoneId }] : [];
              })
            : [];

        return [{ id, name: asString(item.name) || id, price, zoneIds, zones }];
    });
}

/**
 * Choisit ce qu'il faut facturer pour les zones décorées :
 *  1. la façon la moins chère de les couvrir EXACTEMENT avec les entrées de
 *     l'hôte, chaque zone une seule fois (zones seules et combinaisons) ;
 *  2. sinon la combinaison la moins chère qui les COUVRE toutes (sur-ensemble) ;
 *  3. sinon rien (`uncovered`) — l'hôte ne propose pas ces zones.
 * Aucune zone décorée => aucun supplément.
 */
export function resolveZoneCombination(zonePrices: ZonePrice[], usedZoneIds: Iterable<string>): ZonePriceResolution {
    const used = [...new Set(usedZoneIds)];
    if (used.length === 0 || zonePrices.length === 0) {
        return { combination: null, pieces: [], surcharge: 0, uncovered: false };
    }

    const exact = cheapestExactCover(zonePrices, used);
    if (exact) {
        return { ...resolved(exact), uncovered: false };
    }

    const cheapest = zonePrices
        .filter((combination) => used.every((id) => combination.zoneIds.includes(id)))
        .sort((a, b) => priceOf(a) - priceOf(b))[0];

    return cheapest
        ? { ...resolved([cheapest]), uncovered: false }
        : { combination: null, pieces: [], surcharge: 0, uncovered: true };
}

function priceOf(combination: ZonePrice): number {
    return parsePriceNumber(combination.price) ?? 0;
}

function resolved(pieces: ZonePrice[]): Omit<ZonePriceResolution, 'uncovered'> {
    const surcharge = Math.round(pieces.reduce((total, piece) => total + priceOf(piece), 0) * 100) / 100;

    return { combination: pieces.length === 1 ? pieces[0] : null, pieces, surcharge };
}

/**
 * Partition la moins chère des zones décorées en entrées de l'hôte, par
 * programmation dynamique sur les sous-ensembles (quelques zones seulement).
 * Une entrée qui porte une zone non décorée ne s'applique pas. Null si une zone
 * décorée n'apparaît dans aucune entrée applicable.
 */
function cheapestExactCover(zonePrices: ZonePrice[], used: string[]): ZonePrice[] | null {
    const bitOf = new Map(used.map((id, index) => [id, 1 << index]));
    const full = (1 << used.length) - 1;
    const pieces = zonePrices.flatMap((combination) => {
        let mask = 0;
        for (const id of combination.zoneIds) {
            const bit = bitOf.get(id);
            if (bit === undefined) {
                return [];
            }
            mask |= bit;
        }
        return mask ? [{ mask, combination }] : [];
    });

    const best: Array<{ cost: number; pieces: ZonePrice[] } | null> = [{ cost: 0, pieces: [] }];
    for (let covered = 1; covered <= full; covered++) {
        const lowest = covered & -covered;
        let choice: { cost: number; pieces: ZonePrice[] } | null = null;

        // Toute couverture de `covered` contient exactement une entrée portant sa zone de plus bas rang.
        for (const { mask, combination } of pieces) {
            const rest = best[covered ^ mask];
            if ((mask & lowest) === 0 || (mask & covered) !== mask || !rest) {
                continue;
            }
            const cost = rest.cost + priceOf(combination);
            if (!choice || cost < choice.cost) {
                choice = { cost, pieces: [...rest.pieces, combination] };
            }
        }
        best[covered] = choice;
    }

    return best[full]?.pieces ?? null;
}

/** Supplément unitaire numérique d'une combinaison (0 sans combinaison). */
export function zoneSurcharge(combination: ZonePrice | null): number {
    return combination ? (parsePriceNumber(combination.price) ?? 0) : 0;
}

/**
 * Ajoute `surcharge × quantity` à un prix formaté, en conservant son habillage
 * (devise, séparateur). Prix illisible ou supplément nul => prix inchangé.
 */
export function addSurcharge(price: string | undefined, surcharge: number, quantity = 1): string | undefined {
    const amount = parsePriceNumber(price);
    if (price == null || amount == null || surcharge <= 0) {
        return price;
    }
    return formatPriceLike(price, amount + surcharge * Math.max(1, quantity));
}
